/**
 * Coups de pouce — Attendu 1118 « Savoir calculer l'aire des figures planes
 * usuelles : triangle, rectangle, disque » (automatisme 4e).
 *
 * calcul_* (saisie, QCM, V/F) : entrée PARTAGÉE — les quatre formules, la
 * hauteur perpendiculaire (pas le côté penché), le ÷ 2 du triangle, le
 * diamètre à diviser par 2, π laissé dans le résultat, l'unité. L'exemple
 * (revue 2026-09-09) : triangle de base 14 cm, hauteur 6 cm, côté penché
 * 10 cm → 14 × 6 ÷ 2 = 42 cm² (sans lignes de pièges, demande prof).
 * Valeurs hors des plages du générateur (base ≤ 12, hauteurs des triplets).
 *
 * dimension_saisie : on remonte la formule en deux pas. L'exemple : triangle
 * d'aire 30 cm² et de base 12 cm → base × hauteur = 2 × 30 = 60, donc
 * 12 × ? = 60, ? = 5 cm (sans ligne de piège, demande prof). Base 12 hors
 * de la plage du générateur (2 à 10).
 *
 * formule_* (QCM, relier, V/F) : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

const SEG = (a, b, dash = false, couleur = '#1f2937') => `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="${couleur}" stroke-width="1.7" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}/>`;
const COTE = (x, y, txt, couleur = '#b45309', anchor = 'middle') => `<text x="${x}" y="${y}" font-family="system-ui,sans-serif" font-size="11.5" font-weight="700" fill="${couleur}" text-anchor="${anchor}">${txt}</text>`;
const ANGLE_DROIT = (x, y) => `<path d="M ${x + 8} ${y} L ${x + 8} ${y - 8} L ${x} ${y - 8}" fill="none" stroke="#1f2937" stroke-width="1.2"/>`;

// ─── Triangle de base 14 cm, hauteur 6 cm, côté penché 10 cm (échelle 11 px/cm) ─
// A (gauche), B (droite) sur la base ; S le sommet ; H le pied de la hauteur, à 8 cm de B (6-8-10)
const A = [30, 192]; const B = [184, 192]; const H = [96, 192]; const S = [96, 126];
const TRIANGLE = `<polygon points="${A[0]},${A[1]} ${B[0]},${B[1]} ${S[0]},${S[1]}" fill="#EFF6FF" stroke="#1f2937" stroke-width="1.8" stroke-linejoin="round"/>`
    + SEG(H, S, true) + ANGLE_DROIT(H[0], H[1])
    + COTE(107, 208, '14 cm') + COTE(102, 165, '6 cm', '#b45309', 'start') + COTE(152, 150, '10 cm', '#DC2626', 'start');

const SVG_CALCUL = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 292" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : quelle est l'aire d'un triangle de base 14 centimètres, de hauteur 6 centimètres et de côté penché 10 centimètres ? En 3 étapes à faire défiler avec les flèches. Étape 1 : la base mesure 14 centimètres, la hauteur 6 centimètres est perpendiculaire à la base ; le côté penché de 10 centimètres ne sert pas. Étape 2 : aire égale base fois hauteur divisé par 2. Étape 3 : 14 fois 6 divisé par 2 égale 84 divisé par 2 égale 42 centimètres carrés. Réponse : l'aire du triangle est 42 centimètres carrés">
  <style>
    @keyframes cp1118-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1118-pop { opacity: 0; animation: cp1118-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Repère la base et la hauteur</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">La formule du triangle</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Le calcul</text>

  <!-- ── La figure, base et hauteur (étape 1) ────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Triangle : base 14 cm, hauteur 6 cm, côté penché 10 cm.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : quelle est l'aire de ce triangle ?</text>
    ${TRIANGLE}
    <text x="212" y="118" font-size="11" font-weight="700" fill="#1E3A8A">La base : 14 cm.</text>
    <text x="212" y="140" font-size="11" font-weight="700" fill="#1E3A8A">La hauteur : 6 cm,</text>
    <text x="212" y="154" font-size="11" font-weight="700" fill="#1E3A8A">perpendiculaire à la base.</text>
    <text x="212" y="176" font-size="11" font-weight="700" fill="#DC2626">10 cm : le côté penché,</text>
    <text x="212" y="190" font-size="11" font-weight="700" fill="#DC2626">il ne sert pas.</text>
  </g>

  <!-- ── La formule (étape 2) ────────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1118-pop">
      <polygon points="${A[0]},${A[1]} ${B[0]},${B[1]} ${S[0]},${S[1]}" fill="#1D9E75" fill-opacity="0.25"/>
      <text x="8" y="234" font-size="12" font-weight="800" fill="#1D9E75">Aire du triangle = base × hauteur ÷ 2</text>
    </g>
  </g>

  <!-- ── Le calcul et les pièges (étape 3) ───────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1118-pop">
      <text x="8" y="258" font-size="12" font-weight="800" fill="#B45309">Aire = 14 × 6 ÷ 2 = 84 ÷ 2 = 42 cm² ✓</text>
      <text x="8" y="282" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : l'aire du triangle est 42 cm².</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_CALCUL = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Rectangle** : aire = longueur × largeur. **Carré** : aire = côté × côté.',
                "**Triangle** : aire = base × hauteur ÷ 2. La hauteur est perpendiculaire à la base : ce n'est pas le côté penché, même s'il est coté sur la figure. N'oublie pas le ÷ 2.",
                "**Disque** : aire = π × rayon × rayon. Si c'est le diamètre qui est donné, on le divise par 2 d'abord. On laisse π dans le résultat : 3 × 3 = 9, l'aire est 9π cm².",
                "**Vérifie l'unité** : une aire est en cm², dm² ou m², jamais en cm.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_CALCUL },
    ],
};

// ─── Triangle d'aire 30 cm², base 12 cm, hauteur inconnue (échelle 11 px/cm, hauteur 5 cm dessinée) ─
const A2 = [30, 192]; const B2 = [162, 192]; const H2 = [96, 192]; const S2 = [96, 137];
const TRIANGLE_DIM = `<polygon points="${A2[0]},${A2[1]} ${B2[0]},${B2[1]} ${S2[0]},${S2[1]}" fill="#EFF6FF" stroke="#1f2937" stroke-width="1.8" stroke-linejoin="round"/>`
    + SEG(H2, S2, true) + ANGLE_DROIT(H2[0], H2[1])
    + COTE(96, 208, '12 cm') + COTE(103, 170, '?', '#DC2626', 'start');

const SVG_DIMENSION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 326" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : quelle est la hauteur d'un triangle d'aire 30 centimètres carrés dont la base mesure 12 centimètres ? En 3 étapes à faire défiler avec les flèches. Étape 1 : on connaît l'aire, 30 centimètres carrés, et la base, 12 centimètres. Étape 2 : l'aire du triangle est la moitié de base fois hauteur, donc base fois hauteur égale 2 fois 30 égale 60, soit 12 fois point d'interrogation égale 60, donc la hauteur est 60 divisé par 12 égale 5 centimètres. Étape 3 : vérification, 12 fois 5 divisé par 2 égale 30. Réponse : la hauteur est 5 centimètres">
  <style>
    @keyframes cp1118-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1118-pop { opacity: 0; animation: cp1118-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Ce qu'on connaît : l'aire et la base</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">On remonte la formule en deux pas</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On vérifie</text>

  <!-- ── La figure et les données (étape 1) ──────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <text x="8" y="44" font-size="11" font-weight="700" fill="#1e293b">Triangle d'aire 30 cm², dont la base mesure 12 cm.</text>
    <text x="8" y="60" font-size="11" font-weight="700" fill="#1E3A8A">Question : quelle est la hauteur de ce triangle ?</text>
    ${TRIANGLE_DIM}
    <text x="196" y="126" font-size="11" font-weight="700" fill="#1e293b">Aire : 30 cm²</text>
    <text x="196" y="148" font-size="11" font-weight="700" fill="#1E3A8A">Base : 12 cm</text>
    <text x="196" y="170" font-size="11" font-weight="700" fill="#DC2626">Hauteur : ?</text>
    <text x="196" y="184" font-size="11" font-weight="700" fill="#DC2626">C'est ce qu'on cherche.</text>
  </g>

  <!-- ── La formule avec un trou (étape 2) ───────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1118-pop">
      <text x="8" y="234" font-size="11.5" font-weight="700" fill="#1D9E75">L'aire du triangle est la moitié de base × hauteur.</text>
      <text x="8" y="252" font-size="11.5" font-weight="700" fill="#1D9E75">Donc base × hauteur = 2 × 30 = 60, soit 12 × ? = 60.</text>
      <text x="8" y="270" font-size="12" font-weight="800" fill="#1D9E75">hauteur = 60 ÷ 12 = 5 cm ✓</text>
    </g>
  </g>

  <!-- ── Vérification et piège (étape 3) ─────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1118-pop">
      <text x="8" y="294" font-size="11.5" font-weight="700" fill="#B45309">Vérification : 12 × 5 ÷ 2 = 60 ÷ 2 = 30 ✓</text>
      <text x="8" y="316" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : la hauteur du triangle est 5 cm.</text>
    </g>
  </g>
</svg>`.trim();

const AIDE_DIMENSION = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Repars de la formule** et écris-la avec un trou à la place de la dimension cherchée : longueur × … = aire.",
                '**Rectangle** : largeur = aire ÷ longueur. **Triangle** : l\'aire est la moitié de base × hauteur, donc base × hauteur = 2 × aire ; on double l\'aire, puis on divise par la base.',
                '**Carré et disque** : on cherche le nombre qui, multiplié par lui-même, donne l\'aire. 49 = 7 × 7, donc le côté mesure 7 ; une aire de 49π donne un rayon de 7.',
                "**Vérifie** en recalculant l'aire avec la dimension trouvée : on doit retomber sur l'aire de l'énoncé.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_DIMENSION },
    ],
};

const CP_1118 = {
    calcul_saisie: AIDE_CALCUL,
    calcul_qcm_unique: AIDE_CALCUL,
    calcul_tableau_vf: AIDE_CALCUL,
    dimension_saisie: AIDE_DIMENSION,
};

export default CP_1118;
